import {
    Api, DateUtil, DebouncedFunction, FieldUpdateEvent, Logger, LogManager, StringUtil, UserType
} from "@mcleod/core";
import {
    DataSourceMode, DataSourceModeChangeEvent, LookupModelSearchEvent, SaveButton, SeparatorPanel, Textbox,
    ValidationResult
} from "@mcleod/components";
import { AutogenLayoutReportSchedule } from "./autogen/AutogenLayoutReportSchedule";
import { ReportScheduleHistory } from "./ReportScheduleHistory";

export class ReportSchedule extends AutogenLayoutReportSchedule {
    private _log: Logger;
    private debouncedRecalculateNextGenerateDate = DebouncedFunction.create(this.recalcuateNextGenerateDate, 1000);
    private _needsToRecalculateNextGenerationDate = false;
    private recalculatingNextGenerationDate = false;
    private recalcNextGenerationDateFields = this.buildRecalcNextGenerateDateFields();
    private validatingEffectiveDates = false;

    override onLoad() {
        this.setupSeparatorPanels();
        this.setupEffectiveDateValidators();
    }

    private setupSeparatorPanels() {
        const separatorPanelGeneral = new SeparatorPanel(this, false, 100);
        const separatorPanelSchedule = new SeparatorPanel(this.panelLeft, false, 98);
        const separatorPanelNotifications = new SeparatorPanel(this.panelLeft, false, 98);
        const separatorPanelVertical = new SeparatorPanel(this, true, 98);
        this.insertBefore(separatorPanelGeneral, this.panelLeft);
        this.panelLeft.insertBefore(separatorPanelSchedule, this.panelSchedule);
        this.panelLeft.insertBefore(separatorPanelNotifications, this.panelNotification);
        this.insertBefore(separatorPanelVertical, this.panelRight);
    }

    textboxRecurringDateAfterDropdownSelection() {
        this.syncRecurringDateOptions();
    }

    textboxRecurringTimeAfterDropdownSelection() {
        this.syncRecurringTimeOptions();        
    }

    sourceReportScheduleAfterModeChange(event: DataSourceModeChangeEvent) {
        this.syncFieldUpdateListener(event.newMode);
        this.syncButtonHistoryVisibility();
        this.syncRecurringDateOptions();
        this.syncRecurringTimeOptions();
    }

    private syncRecurringDateOptions() {
        const selectedValue = this.textboxRecurringDate.selectedItem?.value;
        this.panelDailyOptions.visible = selectedValue === "D"; // Daily
        this.panelWeekdayMonthOptions.visible = selectedValue === "W"; // Weekday/Month
        this.panelDayMonthOptions.visible = selectedValue === "M"; // Monthly
    }

    private syncRecurringTimeOptions() {
        if (this.textboxRecurringTime.selectedItem?.value == "0") {
            this.textboxBeginningTime.caption = "Time";
            this.textboxBeginningTime.tooltip = "The time report is to be scheduled";
            this.textboxEndingTime.clear()
            this.textboxEndingTime.visible = false;
        } else {
            this.textboxBeginningTime.caption = "Beginning";
            this.textboxBeginningTime.tooltip = "Beginning of the time interval report is to be scheduled";
            this.textboxEndingTime.visible = true;
        }
    }

    textboxNotifyUsersBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.web_user_type = UserType.LME;
    }

    textboxNotifyGroupsBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.user_group_type = "P || B";
    }

    private get displayedReportId(): string {
        return this.sourceReportSchedule.activeRow?.getString("id", null);
    }

    private syncButtonHistoryVisibility() {
        this.buttonHistory.visible = this.updatingExistingRecord();
    }

    buttonHistoryOnClick() {
        this.buttonHistory.makeBusyWhile(ReportScheduleHistory.showInSlideout(this.displayedReportId));
    }

    private updatingExistingRecord(): boolean {
        return this.sourceReportSchedule.mode === DataSourceMode.UPDATE &&
            !StringUtil.isEmptyString(this.displayedReportId);
    }

    private setupEffectiveDateValidators() {
        this.textboxEffectiveFrom.addlValidationCallback = (_value: string, mode: DataSourceMode) => {
            try {
                const result = this.validateEffectiveDates(this.textboxEffectiveFrom, mode);
                if (!this.validatingEffectiveDates) {
                    this.validatingEffectiveDates = true;
                    this.textboxEffectiveTo.validate(true, true);
                }
                return result;
            } finally {
                this.validatingEffectiveDates = false
            }
        };
        this.textboxEffectiveTo.addlValidationCallback = (_value: string, mode: DataSourceMode) => {
            try {
                const result = this.validateEffectiveDates(this.textboxEffectiveTo, mode);
                if (!this.validatingEffectiveDates) {
                    this.validatingEffectiveDates = true;
                    this.textboxEffectiveFrom.validate(true, true);
                }
                return result;
            } finally {
                this.validatingEffectiveDates = false
            }
        };
    }

    private validateEffectiveDates(textbox: Textbox, mode: DataSourceMode) {
        if (mode === DataSourceMode.SEARCH) {
            return null;
        }
        const effectiveFrom = textbox.dataSource.activeRow?.getDate("effective_from", null);
        const effectiveTo = textbox.dataSource.activeRow?.getDate("effective_to", null);
        if (effectiveFrom != null && effectiveTo != null && effectiveTo < effectiveFrom) {
            const effectiveDateResult: ValidationResult = {
                component: textbox,
                isValid: false,
                validationMessage: "Start Date must be before End Date"
            };
            return effectiveDateResult;
        }
        return null;
    }

    private syncFieldUpdateListener(mode: DataSourceMode) {
        if (mode === DataSourceMode.ADD || mode === DataSourceMode.UPDATE) {
            this.sourceReportSchedule.activeRow?.addAfterFieldUpdateListener(this.doAfterFieldUpdate);
        } else {
            this.sourceReportSchedule.activeRow?.removeAfterFieldUpdateListener(this.doAfterFieldUpdate);
        }
    }

    private doAfterFieldUpdate = (event: FieldUpdateEvent) => {
        if (this.recalcNextGenerationDateFields.has(event.fieldName)) {
            this.needsToRecalculateNextGenerationDate = true;
        }
    }

    private get needsToRecalculateNextGenerationDate(): boolean {
        return this._needsToRecalculateNextGenerationDate;
    }

    private set needsToRecalculateNextGenerationDate(value: boolean) {
        this._needsToRecalculateNextGenerationDate = value;
        if (value) {
            this.syncSaveButtonEnabled();
            this.debouncedRecalculateNextGenerateDate();
        }
    }

    private syncSaveButtonEnabled() {
        const saveButton = this.dataHeader?.["saveButton"] as SaveButton;
        if (saveButton == null) {
            return;
        }
        const enable =
            this.needsToRecalculateNextGenerationDate === false &&
            this.recalculatingNextGenerationDate === false;
        saveButton.enableWhenChangesPresent(this.sourceReportSchedule, enable);
        if (enable) {
            saveButton.enabled = this.sourceReportSchedule.activeRow.hasChanged();
        }
    }

    private async recalcuateNextGenerateDate() {
        if (this.recalculatingNextGenerationDate) {
            this.log.debug(
                "Already recalculating next generation date; will recalcuate again after that request is completed");
            return;
        }

        this.log.debug("Recalculating next generation date");
        this.recalculatingNextGenerationDate = true;
        this.needsToRecalculateNextGenerationDate = false;
        const reportSchedule = this.sourceReportSchedule.activeRow;
        if (reportSchedule == null) {
            return;
        }

        try {
            const input = {
                is_active: reportSchedule.getString("is_active"),
                beginning_time: reportSchedule.getDateTime("beginning_time"),
                ending_time: reportSchedule.getDateTime("ending_time"),
                effective_from: reportSchedule.getDate("effective_from"),
                effective_to: reportSchedule.getDate("effective_to"),
                month_day: reportSchedule.getString("month_day"),
                month_frequency: reportSchedule.getString("month_frequency"),
                monthly_date: reportSchedule.getNumber("monthly_date"),
                recurring_date: reportSchedule.getString("recurring_date"),
                recurring_time: reportSchedule.getNumber("recurring_time"),
                run_sunday: reportSchedule.getString("run_sunday"),
                run_monday: reportSchedule.getString("run_monday"),
                run_tuesday: reportSchedule.getString("run_tuesday"),
                run_wednesday: reportSchedule.getString("run_wednesday"),
                run_thursday: reportSchedule.getString("run_thursday"),
                run_friday: reportSchedule.getString("run_friday"),
                run_saturday: reportSchedule.getString("run_saturday")
            }
            this.log.debug("Recalculation input values: %o", input);
            const response = await Api.post("common/reports/report-schedule-recalc-next-gen-date", input);
            const nextGenerateDateString = response?.data[0]?.next_generate_date;
            const nextGenerateDate = !StringUtil.isEmptyString(nextGenerateDateString) ?
                DateUtil.parseDateTime(nextGenerateDateString) :
                undefined;
            reportSchedule.set("next_generate_date", nextGenerateDate);
            this.log.debug("Updated next generate date: %o", nextGenerateDate);
        } finally {
            this.recalculatingNextGenerationDate = false;
            this.log.debug("Finished recalculating next generation date");

            if (this.needsToRecalculateNextGenerationDate) {
                this.recalcuateNextGenerateDate();
            }
            this.syncSaveButtonEnabled();
        }
    }

    private buildRecalcNextGenerateDateFields(): Set<string> {
        return new Set<string>()
            .add("is_active")
            .add("beginning_time")
            .add("ending_time")
            .add("effective_from")
            .add("effective_to")
            .add("month_day")
            .add("month_frequency")
            .add("monthly_date")
            .add("recurring_date")
            .add("recurring_time")
            .add("run_sunday")
            .add("run_monday")
            .add("run_tuesday")
            .add("run_wednesday")
            .add("run_thursday")
            .add("run_friday")
            .add("run_saturday");
    }

    private get log(): Logger {
        if (this._log == null) {
            this._log = LogManager.getLogger("common.reports.ReportSchedule");
        }
        return this._log;
    }
}
